import { ChevronDown, ChevronRight } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'

export const WORKBENCH_PANE_HEADER_HEIGHT_VAR = '--workbench-pane-header-height'

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: var(${WORKBENCH_PANE_HEADER_HEIGHT_VAR}, 1.625rem);
  flex-shrink: 0;
  padding: 0 0.5rem 0 0.375rem;
  user-select: none;
`

const PaneContainer = styled.div<{ $grow: boolean }>`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border);
  ${({ $grow }) =>
    $grow && 'flex: 1; min-height: var(--workbench-pane-min-height, 0);'}

  &:first-child {
    border-top: none;
  }
`

const HeaderButton = styled.button`
  height: 100%;
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: var(--muted-foreground);
  text-align: left;

  &:hover {
    color: var(--foreground);
  }

  &:focus-visible {
    outline: none;
    color: var(--foreground);
    box-shadow: 0 0 0 2px var(--ring);
    border-radius: var(--radius-sm);
  }
`

const PaneTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--workbench-pane-title-size, 0.625rem);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const CountChip = styled.span`
  border-radius: 0.125rem;
  background: var(--accent);
  padding: 0 0.25rem;
  font-size: 0.5625rem;
  font-variant-numeric: tabular-nums;
`

const PaneActions = styled.div`
  button {
    min-height: var(--workbench-pane-action-size, auto);
    min-width: var(--workbench-pane-action-size, auto);
  }

  display: flex;
  align-items: center;
  gap: 0.125rem;
`

const PaneBody = styled.div<{ $grow: boolean; $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  overflow-y: auto;
  ${({ $grow }) => ($grow ? 'flex: 1; min-height: 0;' : 'max-height: 14rem;')}
`

interface WorkbenchPaneProps {
  title: string
  count?: number
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  actions?: React.ReactNode
  grow?: boolean
  keepMounted?: boolean
  children: React.ReactNode
}

export const WorkbenchPane: React.FC<WorkbenchPaneProps> = ({
  title,
  count,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  actions,
  grow = false,
  keepMounted = false,
  children,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const open = controlledOpen ?? uncontrolledOpen
  const [hasOpened, setHasOpened] = useState(open)
  if (open && !hasOpened) setHasOpened(true)

  const toggle = () => {
    onOpenChange?.(!open)
    if (controlledOpen === undefined) setUncontrolledOpen(!open)
  }

  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <PaneContainer $grow={open && grow}>
      <PaneHeader>
        <HeaderButton type='button' aria-expanded={open} onClick={toggle}>
          <Chevron size={12} />
          <PaneTitle>{title}</PaneTitle>
          {count !== undefined && <CountChip>{count}</CountChip>}
        </HeaderButton>
        {actions !== undefined && <PaneActions>{actions}</PaneActions>}
      </PaneHeader>
      {(open || (keepMounted && hasOpened)) && (
        <PaneBody $grow={grow} $open={open}>
          {children}
        </PaneBody>
      )}
    </PaneContainer>
  )
}
